import React from 'react';
import type { ContentLinkSegment, RichText } from '@/content/types';
import { isRichText } from '@/content/types';
import styles from './ContentRichText.module.scss';

export interface ContentRichTextProps {
  content: RichText | string;
  /** Optional BEM hook (e.g. footer meta link variant); base link styles live on content-rich-text__link */
  linkClassName?: string;
  className?: string;
}

const NEW_TAB_PHRASE = /opens in new tab/i;

const mentionsNewTab = (label: string): boolean => NEW_TAB_PHRASE.test(label);

const resolveAriaLabel = (
  segment: ContentLinkSegment,
  isExternal: boolean
): string | undefined => {
  if (!segment.ariaLabel) {
    return undefined;
  }

  if (!isExternal || mentionsNewTab(segment.ariaLabel)) {
    return segment.ariaLabel;
  }

  return `${segment.ariaLabel} (opens in new tab)`;
};

const renderLinkContent = (segment: ContentLinkSegment) => {
  const inner = segment.wrap === 'ins' ? <ins>{segment.text}</ins> : segment.text;
  return inner;
};

export const ContentRichText: React.FC<ContentRichTextProps> = ({
  content,
  linkClassName,
  className,
}) => {
  if (!isRichText(content)) {
    return className ? <span className={className}>{content}</span> : <>{content}</>;
  }

  const linkClasses = [styles['content-rich-text__link'], linkClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={className}>
      {content.map((segment, index) => {
        if (typeof segment === 'string') {
          return <React.Fragment key={index}>{segment}</React.Fragment>;
        }

        const isExternal = segment.external !== false;
        const ariaLabel = resolveAriaLabel(segment, isExternal);
        const showVisuallyHiddenNewTab = isExternal && !segment.ariaLabel;

        return (
          <a
            key={index}
            href={segment.href}
            className={linkClasses}
            {...(isExternal
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
            {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          >
            {renderLinkContent(segment)}
            {showVisuallyHiddenNewTab ? (
              <span className="visually-hidden"> (opens in new tab)</span>
            ) : null}
          </a>
        );
      })}
    </span>
  );
};

export default ContentRichText;
